import { createContext, useContext } from 'react';

/**
 * A helper to create a Context and Provider with no upfront default value.
 *
 * @param displayName The name of the context. Helps with debugging through
 * React DevTools - without this, the context will show up as `useCtx` with an
 * inner value of `Context`. This changes the inner value to the display name.
 *
 * @returns A tuple with the `useCtx` hook and the `Provider` component.
 * `useCtx` will throw an error on missing context by default, but can allow
 * empty contexts if the `throwOnMissing` option is set to `false`.
 */
export function createCtx<A extends object | null>(displayName: string, defaultValue?: A) {
  const ctx = createContext(defaultValue);
  function useCtx(options?: { throwOnMissing: true }): A;
  function useCtx(options: { throwOnMissing: false }): A | null;
  function useCtx(options = { throwOnMissing: true }) {
    const c = useContext(ctx) || null;
    if (!c && options.throwOnMissing) {
      throw new Error('useCtx must be inside a Provider with a value');
    }
    return c;
  }
  ctx.displayName = displayName;
  return [useCtx, ctx.Provider] as const; // 'as const' makes TypeScript infer a tuple
}
